import { useEffect, useState } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../../api";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const OvertimePay = () => {
  const navigate = useNavigate();
  const itemId = "overtime-pay";
  const fieldOrder = ["date", "employeeName", "employeeSignature"];
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    } else {
      // No saved draft -> try to prefill employee name from profile
      (async () => {
        try {
          const resp = await API.get(`/api/registration/${accountId}/profile`);
          const profile = resp.data || {};
          const first = profile.first_name || localStorage.getItem('signup_first_name') || "";
          const last = profile.last_name || localStorage.getItem('signup_last_name') || "";
          const name = `${first} ${last}`.trim();
          if (name) {
            setFormData((prev) => ({ ...prev, employeeName: name }));
            localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify({ ...formData, employeeName: name }));
          }
        } catch (err) {
          // ignore if profile not available
        }
      })();
    }
  }, []);

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const errors = {};
    if (!formData.date) errors.date = "This field is required";
    else if (!isDateToday(formData.date)) errors.date = "Date must be today";
    if (!String(formData.employeeName || "").trim()) errors.employeeName = "This field is required";
    if (!formData.employeeSignature) errors.employeeSignature = "Signature is required";

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const newStatus = allEmpty ? "incomplete" : allFilled ? "complete" : "in-progress";
    const payload = { status: newStatus, formData };
    if (accountId && newStatus === 'complete') {
      axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch(err => console.error('Failed to save item data:', err))
        .finally(() => {
          axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} }).catch(() => {});
          showSuccess("Form completed successfully!");
          navigate('/register');
        });
    } else {
      if (accountId) axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} }).catch(() => {});
      navigate('/register');
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (String(value || "").trim()) delete next[field];
      return next;
    });
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>
              Hours of Work and Overtime Pay
            </h2>
          </header>

          <section className="form-card-content">
            {err && (<div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>)}
            <p className="form-intro">
              <strong>Disclaimer:</strong> This resource has been prepared to help employees and employers understand some of the minimum rights and obligations established under the Employment Standards Act, 2000 (ESA) and regulations. It is not legal advice. It is not intended to replace the ESA or regulations and reference should always be made to the official version of the legislation. Although we endeavor to ensure that the information in this resource is as current and accurate as possible, errors do occasionally occur. The ESA provides minimum standards only. Some employees may have greater rights under an employment contract, collective agreement, the common law or other legislation. 
            </p>
            <p className="form-intro">
                <a href="https://www.ontario.ca/laws/statute/00e41?_ga=2.173408935.584403115.1583168406-1699692134.1480627715" class="red-link" target="_blank" rel="noopener noreferrer">
                    The Employment Standards Act, 2000
                </a> ( ESA) sets minimum standards for most workplaces in Ontario, including hours of work and overtime pay. This information sheet provides information about the general rules in Ontario about hours of work and overtime pay.
            </p>
            <p className="form-intro">
                Under ESA, some jobs or industries have special rules or exemptions for hours of work and overtime. For more information and to learn if your job has special rules or exemptions, go to 
                <a href="https://www.ontario.ca/document/your-guide-employment-standards-act-0/educational-resources" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "}  Ontario.ca/ESAtools
                </a>.
            </p>
            <p className="form-intro topic1">
              Hours of work
            </p>
            <p className="form-intro">
              You cannot be required to work more than eight hours a day or the number of hours in your regular work day if your regular work day is longer than eight hours. You also cannot be required to work more than 48hours in a week.
            </p>
            <p className="form-intro topic1">
              Rest Period and eating periods
            </p>
            <p className="form-intro paragraph-space">
              Your employer must give you a 30-minute eating period after working no more than five hours of work, or two shorter eating periods equal to 30 minutes within a five-hour period, if the employer and employee agree.
            </p>
            <p className="form-intro paragraph-space">
              Your employer must give you 11 consecutive hours of work in each 24-hour period. You must also have 24 straight hours off work each work week, or 48 straight hours off every two work weeks
            </p>
            <p className="form-intro paragraph-space">
              Your employer must give you eight hours off between shifts. this does not apply if the total time worked on both shifts is no more than 13 hours, or you and your employer have agreed in writing that you will receive less than eight hours off between shifts.
            </p>
            <p className="form-intro paragraph-space">
              To calculate these periods, try the Hours of Work and Overtime Tool at
              <a href="https://www.ontario.ca/document/your-guide-employment-standards-act-0/educational-resources" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "}  Ontario.ca/ESAtools
                </a>.
            </p>
            <p className="form-intro topic1">
              Excess hours of work
            </p>
            <p className="form-intro paragraph-space">
              You and your employer can agree in writing that you will work more than :
            </p>
            <ul className="form-bullets">
              <li>• eight hours a day or their established regular workday- if its longer than eight hours:</li>
              <li>• 48 hours a week.</li>
              <li><strong>You do not have to agree to work excess hours.</strong></li>
            </ul>
            <p className="form-intro paragraph-space">
              If you agree to work excess hours, you cannot work more than the number of hours shown in your agreement.
            </p>
            <p className="form-intro paragraph-space">
              You can cancel this agreement by giving your employer two weeks' written notice, Your employer can also cancel the agreement by giving reasonable notice. Once your agreement is cancelled you can no longer work excess hours.
            </p>
            <p className="form-intro paragraph-space">
              If you are represented by a trade union your union can make agreements with your employer on your behalf.
            </p>
            <p className="form-intro paragraph-space">
              For more information on hours of work, including excess hours agreements go to
              <a href="https://www.ontario.ca/document/your-guide-employment-standards-act-0/hours-work" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "}  Ontario.ca/hoursofwork
                </a>.
            </p>
            <p className="form-intro topic1">
              Overtime pay
            </p>
            <p className="form-intro paragraph-space">
              Generally, your employer must pay you overtime pay if you work more than 44 hours in a week. Overtime pay is 1<sup>1/2</sup> hours off each overtime hour worked. The time off must be taken within three months of being earned.
            </p>
            <p className="form-intro topic1">
              Overtime averaging
            </p>
            <p className="form-intro paragraph-space">
              You and your employer can agree in writing to average your hours over two, three or four weeks to determine overtime pay, if any. This means your overtime pay is based on the average number of hours you worked in the period set out in the agreement rather than each week. You do not have to agree to overtime averaging if you do not want to.
            </p>
            <p className="form-intro paragraph-space">
              These agreements must contain a start date and an expiry date and generally cannot last longer than two years. Generally, you or your employer cannot end the averaging agreement before the expiry date, unless you and your employer both agree, in writing, to end it. If you are represented by a trade union, your union can make agreements with your employer on your behalf. These agreements will last no longer than when the next collective agreement takes effect.
            </p>
            <p className="form-intro paragraph-space">
              For more information about overtime and averaging agreements visit
              <a href="https://www.ontario.ca/document/your-guide-employment-standards-act-0/overtime-pay" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "}  Ontario.ca/overtime
                </a>.
            </p>
            <p className="form-intro topic1">
              OYou cannot be punished for asking about or using your ESA rights 
            </p>
            <p className="form-intro paragraph-space">
              If you ask about or exercise your rights under the ESA your employer cannot punish you in any way, including by ending your employment.
            </p>
            <p className="form-intro topic1">
              You cannot agree to give up your rights 
            </p>
            <p className="form-intro paragraph-space">
              You cannot agree or sign a contract to give up any of your rights under the ESA. These agreements or contracts are invalid. For example, if you sign a contract saying your employer does not have to pay you overtime, this agreement would be invalid. 
            </p>
            <p className="form-intro topic1">
              For more information or to file a claim 
            </p>
            <p className="form-intro paragraph-space">
              For more information about the ESA visit
              <a href="https://www.ontario.ca/document/your-guide-employment-standards-act-0" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "} Ontario.ca/ESAquide
                </a>.
            </p>
            <p className="form-intro paragraph-space">
              If you have questions about the ESA you may also call the Employment Standards Information Centre at 416-326-7160, toll free at 1-800-531-5551, or TTY 1-866-567-8893. Information is available in many languages. 
            </p>
            <p className="form-intro paragraph-space">
              If you believe that you have not received your rights under the ESA, you can file a claim with the Ministry of Labour, Training and Skills Development at
              <a href="https://www.ontario.ca/document/your-guide-employment-standards-act-0/filing-claim" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "} Ontario.ca/ESAclaims
                </a>.
            </p>
          </section>
          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) =>
                  handleInputChange("employeeName", e.target.value)
                }
                required
              />
              {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            {/* <label>Employee's Signature*</label> */}
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OvertimePay;
